import { HTMLProps } from 'react';

import { makeStyles } from '@material-ui/core';

import classNames from 'classnames';
import { diffJson } from 'diff';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    fontFamily: 'var(--encore-bodyMono-font-stack, monospace)',
    overflow: 'auto',
    fontSize: 'var(--encore-text-size-smaller-2)',
  },
  column: {
    flex: 1,
    minWidth: 0,
    overflow: 'clip',
    padding: theme.spacing(0.5, 0),
  },
  line: {
    padding: theme.spacing(0.25, 1),
    whiteSpace: 'pre',
    minHeight: theme.spacing(2),
  },
  removed: {
    background:
      'color-mix(in srgb, var(--background-negative-subdued-base) 80%, transparent)',
    borderLeft: `3px solid var(--essential-negative)`,
  },
  added: {
    background:
      'color-mix(in srgb, var(--background-positive-subdued-base) 80%, transparent)',
    borderLeft: `3px solid var(--essential-positive)`,
  },
  changed: {
    background:
      'color-mix(in srgb, var(--background-warning-subdued-base) 80%, transparent)',
    borderLeft: `3px solid var(--essential-warning)`,
  },
  divider: {
    width: 1,
    background: 'var(--decorative-subdued)',
  },
  empty: {},
}));

type SideBySideRow = {
  left: { content: string; type: 'removed' | 'changed' | 'same' | 'empty' };
  right: { content: string; type: 'added' | 'changed' | 'same' | 'empty' };
};

const generateSideBySideDiff = (
  oldValue?: object,
  newValue?: object,
): SideBySideRow[] => {
  const diff = diffJson(oldValue ?? '', newValue ?? '');

  // Flatten diff into lines
  type DiffPart = { added?: boolean; removed?: boolean; value: string };
  const oldLines: { line: string; group: DiffPart }[] = [];
  const newLines: { line: string; group: DiffPart }[] = [];
  diff.forEach(part => {
    const lines = part.value.split('\n');
    // remove possible trailing "" from final split line
    const onlyLines =
      lines[lines.length - 1] === '' ? lines.slice(0, -1) : lines;
    onlyLines.forEach(line => {
      if (part.removed) {
        oldLines.push({ line, group: part });
      } else if (part.added) {
        newLines.push({ line, group: part });
      } else {
        // unchanged
        oldLines.push({ line, group: part });
        newLines.push({ line, group: part });
      }
    });
  });

  // Merge aligned lines into side-by-side rows.
  // If counts mismatch, pair up as diffs.
  const rows: SideBySideRow[] = [];
  let i = 0;
  let j = 0;
  while (i < oldLines.length || j < newLines.length) {
    const oldItem = oldLines[i];
    const newItem = newLines[j];

    if (
      oldItem &&
      newItem &&
      oldItem.line === newItem.line &&
      !oldItem.group.removed &&
      !newItem.group.added
    ) {
      // unchanged line
      rows.push({
        left: { content: oldItem.line, type: 'same' },
        right: { content: newItem.line, type: 'same' },
      });
      i++;
      j++;
    } else if (oldItem && oldItem.group.removed) {
      // removed line, try to pair with add
      if (newItem && newItem.group.added) {
        rows.push({
          left: { content: oldItem.line, type: 'changed' },
          right: { content: newItem.line, type: 'changed' },
        });
        i++;
        j++;
      } else {
        rows.push({
          left: { content: oldItem.line, type: 'removed' },
          right: { content: '', type: 'empty' },
        });
        i++;
      }
    } else if (newItem && newItem.group.added) {
      rows.push({
        left: { content: '', type: 'empty' },
        right: { content: newItem.line, type: 'added' },
      });
      j++;
    } else {
      // fallback (should not happen, but just in case)
      break;
    }
  }

  return rows;
};

export interface JsonSideBySideProps extends HTMLProps<HTMLDivElement> {
  oldValue?: object | undefined;
  newValue?: object | undefined;
}

export const JsonSideBySide = ({
  oldValue,
  newValue,
  className,
  ...rest
}: JsonSideBySideProps) => {
  const classes = useStyles();

  const rows = generateSideBySideDiff(oldValue, newValue);

  return (
    <div className={classNames(classes.root, className)} {...rest}>
      <div className={classes.column}>
        {rows.map((row, index) => (
          <div
            key={index}
            className={classNames(classes.line, {
              [classes.removed]: row.left.type === 'removed',
              [classes.changed]: row.left.type === 'changed',
              [classes.empty]: row.left.type === 'empty',
            })}
          >
            {row.left.content || '\u00A0'}
          </div>
        ))}
      </div>
      <div className={classes.divider} />
      <div className={classes.column}>
        {rows.map((row, index) => (
          <div
            key={index}
            className={classNames(classes.line, {
              [classes.added]: row.right.type === 'added',
              [classes.changed]: row.right.type === 'changed',
              [classes.empty]: row.right.type === 'empty',
            })}
          >
            {row.right.content || '\u00A0'}
          </div>
        ))}
      </div>
    </div>
  );
};
